import type { EnrichedContent, EnrichRequest, ItineraryDay, ItineraryEditor, ItineraryNode, ItinerarySegment, PointDescriptionCompletionRequest, PointDescriptionCompletionResult, SummarySlot, SummarySource, TimeSlot } from "../types";
import { buildInheritedDayNarratives, editableSummary, hasTextContent, storedSummary } from "./itineraryNarrativeService";
export { editableSummary, hasTextContent, storedSummary } from "./itineraryNarrativeService";

/** 摘要槽位与编辑器字段的一一映射，单项重写和保存前补全共同使用。 */
export const summaryFieldBySlot = {
  morning: "morningSummary",
  afternoon: "afternoonSummary",
  evening: "eveningSummary",
  full_day: "fullDaySummary",
  daytime: "summary",
} as const;

export const summarySourceFieldBySlot = {
  morning: "morningSummarySource",
  afternoon: "afternoonSummarySource",
  evening: "eveningSummarySource",
  full_day: "fullDaySummarySource",
  daytime: "summarySource",
} as const;

export type AutoSummarySlot = TimeSlot | "full_day";
export type PendingSummaryRewriteSlots = Record<string, AutoSummarySlot[]>;
export type PendingPointRewriteIds = Record<string, string[]>;
export const autoSummarySlots = ["morning", "afternoon", "evening"] as const;
export const allSummarySlots = [...autoSummarySlots, "full_day"] as const;

/** 当前正文模式统一决定编辑、生成与保存需要处理的槽位。 */
export function activeSummarySlots(day: Pick<ItineraryDay, "isFullDay">): AutoSummarySlot[] {
  return day.isFullDay ? ["full_day"] : [...autoSummarySlots];
}

/** 全天首次接管客户可见原文及介绍；主动空串不回退旧稿。 */
export function effectiveSummaryText(day: ItineraryDay, slot: SummarySlot): string | null | undefined {
  if (slot !== "full_day" || day.fullDaySummary != null) return day[summaryFieldBySlot[slot]];
  return buildInheritedDayNarratives({ ...day, legacyCompatibility: day.legacyCompatibility ?? false })
    .flatMap((part) => part.paragraphs.map((paragraph) => paragraph.content)).join("<br><br>");
}

/** 继承正文中任一人工稿都受保护；仅全部已融合时才继承融合标记。 */
export function effectiveSummarySource(day: ItineraryDay, slot: SummarySlot): SummarySource | null | undefined {
  if (slot !== "full_day" || day.fullDaySummary != null) return day[summarySourceFieldBySlot[slot]];
  const slots = autoSummarySlots.filter((item) => hasTextContent(day[summaryFieldBySlot[item]]));
  if (!slots.length) return day.summarySource;
  const sources = slots.map((item) => day[summarySourceFieldBySlot[item]]);
  const composed = sources.every(isComposedSummarySource);
  return sources.some(isManualSummarySource) ? (composed ? "manual_composed" : "manual")
    : composed ? "ai_composed" : "ai_generated";
}

/** 读取节点实际参与的专业；有明确分流时只采用关联轨道，否则采用行程共同专业。 */
export function pointProfessionContext(editor: ItineraryEditor, dayId: string, nodeId: string) {
  const day = editor.days.find((item) => item.id === dayId);
  const tracks = day?.parallelTracks?.tracks ?? [];
  const fallback = editor.professionSettings?.majors.length ? editor.professionSettings.majors
    : editor.days.flatMap((item) => item.parallelTracks?.tracks.map((track) => ({ id: track.majorId, name: track.title })) ?? []);
  const ids = tracks.length
    ? tracks.filter((track) => track.nodeIds.includes(nodeId)).map((track) => track.majorId)
    : fallback.map((major) => major.id);
  const names = new Map(fallback.map((major) => [major.id, major.name]));
  for (const track of tracks) names.set(track.majorId, names.get(track.majorId) ?? track.title);
  return [...new Set(ids)].sort().map((id) => ({ id, name: names.get(id) ?? id }));
}

/** 点位介绍只依赖该次到访及参与专业，其他点介绍或交通回填不使其过期。 */
export function pointDescriptionFingerprint(editor: ItineraryEditor, dayId: string, nodeId: string): string {
  const node = editor.days.find((day) => day.id === dayId)?.nodes.find((item) => item.id === nodeId);
  return JSON.stringify({ itineraryId: editor.id, dayId, node: node && {
    id: node.id, nodeType: node.nodeType, pointSpuId: node.pointSpuId, title: node.title,
    description: node.description,
  }, professions: pointProfessionContext(editor, dayId, nodeId) });
}

/** 人工正文只能由明确的重写动作替换。 */
export function isManualSummarySource(source?: SummarySource | null): boolean {
  return source === "manual" || source === "manual_composed";
}

/** 融合正文已包含点位学习材料，不再追加旧版详情。 */
export function isComposedSummarySource(source?: SummarySource | null): boolean {
  return source === "ai_composed" || source === "manual_composed";
}

/** 人工编辑保留正文是否已经融合的语义。 */
export function manualSummarySource(source?: SummarySource | null): SummarySource {
  return isComposedSummarySource(source) ? "manual_composed" : "manual";
}

/** 只纳入文案消费的节点事实，图片和主数据展示标记不触发重写。 */
function nodeMaterial(node: ItineraryNode) {
  return { id: node.id, nodeType: node.nodeType, pointSpuId: node.pointSpuId,
    stayAreaId: node.stayAreaId, organizationSiteId: node.organizationSiteId,
    title: node.title, description: node.description, timeSlot: node.timeSlot,
    sortOrder: node.sortOrder, planStartAt: node.planStartAt, planEndAt: node.planEndAt };
}

/** 只纳入已选交通事实，不让查询备选、费用或查询时间使正文失效。 */
function segmentMaterial(segment: ItinerarySegment) {
  return { id: segment.id, startDayId: segment.startDayId, endDayId: segment.endDayId,
    fromNodeId: segment.fromNodeId, toNodeId: segment.toNodeId, routeSeq: segment.routeSeq,
    majorId: segment.majorId, routeType: segment.routeType, transportMode: segment.transportMode,
    routeCode: segment.routeCode, departureAt: segment.departureAt, arrivalAt: segment.arrivalAt,
    distanceKm: segment.distanceKm, durationMinutes: segment.durationMinutes,
    segmentDescription: segment.segmentDescription, remark: segment.remark,
    selected: segment.sourcePayload?.selected && {
      label: segment.sourcePayload.selected.label, routeCode: segment.sourcePayload.selected.routeCode,
      fromName: segment.sourcePayload.selected.fromName, toName: segment.sourcePayload.selected.toName,
      departureAt: segment.sourcePayload.selected.departureAt, arrivalAt: segment.sourcePayload.selected.arrivalAt,
      durationMinutes: segment.sourcePayload.selected.durationMinutes, distanceKm: segment.sourcePayload.selected.distanceKm,
    } };
}

/** 根据业务当地时刻确定实际跨越的三个时段。 */
function segmentOccursInSlot(segment: ItinerarySegment, dayId: string, slot: SummarySlot): boolean {
  const minutes = (value?: string | null) => {
    const match = value?.match(/(?:T|\s)([01]\d|2[0-3]):([0-5]\d)/);
    return match ? Number(match[1]) * 60 + Number(match[2]) : undefined;
  };
  const departure = minutes(segment.departureAt);
  const arrival = minutes(segment.arrivalAt);
  const start = slot === "morning" ? 0 : slot === "afternoon" ? 720 : slot === "evening" ? 1080 : 0;
  const end = slot === "morning" ? 720 : slot === "afternoon" || slot === "daytime" ? 1080 : 1440;
  if (segment.startDayId === dayId && segment.endDayId === dayId && departure !== undefined && arrival !== undefined) {
    return departure < end && arrival >= start;
  }
  return (segment.startDayId === dayId && departure !== undefined && departure >= start && departure < end)
    || (segment.endDayId === dayId && arrival !== undefined && arrival >= start && arrival < end);
}

/** 固定目标时段及真实前后衔接，供失效判断和响应隔离共用。 */
function summaryMaterials(editor: ItineraryEditor, dayId: string, slot: SummarySlot) {
  const day = editor.days.find((item) => item.id === dayId);
  const nodes = day?.nodes.filter((node) => slot === "full_day" || (slot === "daytime" ? node.timeSlot !== "evening" : node.timeSlot === slot)) ?? [];
  const ids = new Set(nodes.map((node) => node.id));
  const dayNumbers = new Map(editor.days.map((item) => [item.id, item.dayNumber]));
  const segments = editor.segments.filter((segment) => ids.has(segment.fromNodeId) || ids.has(segment.toNodeId)
    || (slot === "full_day" && day && (dayNumbers.get(segment.startDayId) ?? Infinity) <= day.dayNumber
      && (dayNumbers.get(segment.endDayId) ?? -Infinity) >= day.dayNumber)
    || segmentOccursInSlot(segment, dayId, slot));
  const endpointIds = new Set(segments.flatMap((segment) => [segment.fromNodeId, segment.toNodeId]));
  const professionIds = new Set(nodes.flatMap((node) => pointProfessionContext(editor, dayId, node.id).map((item) => item.id)));
  for (const segment of segments) if (segment.majorId) professionIds.add(segment.majorId);
  const majorNames = new Map((editor.professionSettings?.majors ?? []).map((major) => [major.id, major.name]));
  for (const item of editor.days.flatMap((entry) => entry.parallelTracks?.tracks ?? [])) {
    majorNames.set(item.majorId, majorNames.get(item.majorId) ?? item.title);
  }
  return { dayId, dayNumber: day?.dayNumber, dayDate: day?.dayDate,
    professions: [...professionIds].sort().map((id) => ({ id, name: majorNames.get(id) ?? id })),
    nodes: nodes.map(nodeMaterial), segments: segments.map(segmentMaterial),
    endpoints: editor.days.flatMap((item) => item.nodes.filter((node) => endpointIds.has(node.id)).map((node) => ({
      // 端点用于交通衔接；介绍只由上方本时段 nodes 提供，邻时段介绍回填不能使人工正文过期。
      dayId: item.id, dayDate: item.dayDate, node: { ...nodeMaterial(node), description: undefined },
    }))),
    tracks: editor.days.flatMap((item) => (item.parallelTracks?.tracks ?? [])
      .filter((track) => track.nodeIds.some((id) => ids.has(id) || endpointIds.has(id)))
      .map((track) => ({ dayId: item.id, ...track }))).sort((a, b) => a.id.localeCompare(b.id)),
  };
}

/** 无安排的时段允许留空，不生成“无安排”等占位正文。 */
export function hasSummaryArrangement(editor: ItineraryEditor, dayId: string, slot: SummarySlot): boolean {
  const materials = summaryMaterials(editor, dayId, slot);
  return Boolean(materials.nodes.length || materials.segments.length);
}

/** 汇总待生成与缺失文案；孤立端点和服务端已确认的空结果不强制填字。 */
export function pendingContentSlots(
  editor: ItineraryEditor,
  pending: PendingSummaryRewriteSlots = {},
  pendingPoints: PendingPointRewriteIds = {},
): PendingSummaryRewriteSlots {
  const result: PendingSummaryRewriteSlots = {};
  for (const day of editor.days) {
    if (day.legacyCompatibility && !pending[day.id]?.length && !pendingPoints[day.id]?.length) continue;
    const slots = activeSummarySlots(day).filter((slot) => !isManualSummarySource(effectiveSummarySource(day, slot)) && (pending[day.id]?.includes(slot)
      || (slot === "full_day" && day.fullDaySummary == null && pending[day.id]?.some((item) => item !== "full_day"))
      || day.nodes.some((node) => pendingPoints[day.id]?.includes(node.id) && (slot === "full_day" || node.timeSlot === slot))
      || day.nodes.some((node) => (slot === "full_day" || node.timeSlot === slot) && node.nodeType === "point" && node.pointSpuId?.trim() && !hasTextContent(node.description))
      || ((day.nodes.some((node) => (slot === "full_day" || node.timeSlot === slot) && ["point", "custom"].includes(node.nodeType))
          || summaryMaterials(editor, day.id, slot).segments.length > 0)
        && effectiveSummarySource(day, slot) !== "ai_composed"
        && !hasTextContent(effectiveSummaryText(day, slot)))));
    if (slots.length) result[day.id] = slots;
  }
  return result;
}

/** 专业参与集合变化时，返回必须按新专业重写的既有点位介绍。 */
export function collectAffectedPointDescriptions(previous: ItineraryEditor, next: ItineraryEditor): PendingPointRewriteIds {
  const affected: PendingPointRewriteIds = {};
  for (const nextDay of next.days) {
    const previousDay = previous.days.find((day) => day.id === nextDay.id);
    if (!previousDay) continue;
    for (const node of nextDay.nodes) {
      if (node.nodeType !== "point" || !node.pointSpuId?.trim()) continue;
      const before = previousDay.nodes.find((item) => item.id === node.id);
      if (!before || before.nodeType !== "point" || before.pointSpuId !== node.pointSpuId) continue;
      const oldContext = pointProfessionContext(previous, previousDay.id, before.id);
      const newContext = pointProfessionContext(next, nextDay.id, node.id);
      if (JSON.stringify(oldContext) === JSON.stringify(newContext)) continue;
      (affected[nextDay.id] ??= []).push(node.id);
    }
  }
  return affected;
}

/** 保留节点、介绍、专业和实际交通的请求快照，排除正文自身回填。 */
export function summaryMaterialFingerprint(editor: ItineraryEditor, dayId: string, slot: SummarySlot): string {
  return JSON.stringify({ isFullDay: editor.days.find((day) => day.id === dayId)?.isFullDay,
    materials: summaryMaterials(editor, dayId, slot) });
}

/** 比较三个时段真实材料；图片、正文和查询备选变更不会触发自动循环。 */
export function collectAffectedSummarySlots(previous: ItineraryEditor, next: ItineraryEditor): PendingSummaryRewriteSlots {
  const affected: PendingSummaryRewriteSlots = {};
  for (const dayId of new Set([...previous.days, ...next.days].map((day) => day.id))) {
    const previousDay = previous.days.find((day) => day.id === dayId);
    const nextDay = next.days.find((day) => day.id === dayId);
    const firstLegacyProcessing = previousDay?.legacyCompatibility === true
      && nextDay?.legacyCompatibility === false;
    const slots = allSummarySlots.filter((slot) => (slot !== "full_day" || previousDay?.isFullDay || nextDay?.isFullDay
      || previousDay?.fullDaySummary != null || nextDay?.fullDaySummary != null) && (
      (firstLegacyProcessing
        && nextDay && activeSummarySlots(nextDay).includes(slot)
        && !isManualSummarySource(nextDay?.[summarySourceFieldBySlot[slot]])
        && nextDay?.[summarySourceFieldBySlot[slot]] !== "ai_composed"
        && (hasTextContent(previousDay?.[summaryFieldBySlot[slot]])
          || hasSummaryArrangement(previous, dayId, slot)))
      ||
      (hasSummaryArrangement(previous, dayId, slot) || hasSummaryArrangement(next, dayId, slot))
      && JSON.stringify(summaryMaterials(previous, dayId, slot)) !== JSON.stringify(summaryMaterials(next, dayId, slot))
    ));
    if (slots.length) affected[dayId] = slots;
  }
  return affected;
}

/** 当天正文或写作事实进入新编辑流程时关闭草稿兼容，保存完成前不影响客户快照。 */
export function markEditedDayContent(previous: ItineraryEditor, next: ItineraryEditor): ItineraryEditor {
  const affected = collectAffectedSummarySlots(previous, next);
  return { ...next, days: next.days.map((day) => {
    const before = previous.days.find((item) => item.id === day.id);
    if (!before) return day;
    const changed = affected[day.id]?.length || day.summary !== before.summary
      || allSummarySlots.some((slot) => day[summaryFieldBySlot[slot]] !== before[summaryFieldBySlot[slot]]
        || day[summarySourceFieldBySlot[slot]] !== before[summarySourceFieldBySlot[slot]]);
    return !before.legacyCompatibility || changed ? { ...day, legacyCompatibility: false } : day;
  }) };
}

/** 从统一 AI 响应中提取指定日、指定摘要槽位的有效正文。 */
export function generatedSummary(
  result: EnrichedContent,
  dayId: string,
  slot: SummarySlot,
): string | null {
  const field = summaryFieldBySlot[slot];
  const value = result.days.find((item) => item.dayId === dayId)?.[field];
  return hasTextContent(value) ? storedSummary(editableSummary(value)) : null;
}

/** 只回填目标日目标槽位；其他日期、字段和人工内容保持不变。 */
export function applyGeneratedSummary(
  editor: ItineraryEditor,
  dayId: string,
  slot: SummarySlot,
  result: EnrichedContent,
  expectedValue?: string | null,
): ItineraryEditor | null {
  const field = summaryFieldBySlot[slot];
  const sourceField = summarySourceFieldBySlot[slot];
  const generated = generatedSummary(result, dayId, slot);
  if (!generated) return null;
  const currentDay = editor.days.find((day) => day.id === dayId);
  if (!currentDay) return null;
  if (expectedValue !== undefined && (effectiveSummaryText(currentDay, slot) ?? null) !== (expectedValue ?? null)) return null;
  return {
    ...editor,
    days: editor.days.map((day) => day.id === dayId
      ? { ...day, legacyCompatibility: false, [field]: generated, [sourceField]: slot !== "daytime" && result.days.find((item) => item.dayId === dayId)?.composedSlots?.includes(slot) ? "ai_composed" : "ai_generated" }
      : day),
  };
}

/** 构造编辑按钮与保存前补全共用的单日单时段 AI 请求。 */
export function requestDaySummaryRewrite(
  editor: ItineraryEditor,
  dayId: string,
  slot: SummarySlot,
  enrich: (request: EnrichRequest, signal?: AbortSignal) => Promise<EnrichedContent>,
  signal?: AbortSignal,
): Promise<EnrichedContent> {
  return enrich({ scope: "day", dayId, summarySlot: slot, ...(slot !== "daytime" ? { composePoints: true } : {}), editor }, signal);
}

export type SummaryCompositionOptions = {
  dayId: string;
  slot: SummarySlot;
  getEditor: () => ItineraryEditor;
  onChange: (editor: ItineraryEditor) => void;
  enrich: (request: EnrichRequest, signal?: AbortSignal) => Promise<EnrichedContent>;
  completePointDescriptions?: (request: PointDescriptionCompletionRequest, signal?: AbortSignal) => Promise<PointDescriptionCompletionResult>;
  signal?: AbortSignal;
  automatic?: boolean;
  isCurrent?: () => boolean;
  descriptionsPrepared?: boolean;
  forceManualRewrite?: boolean;
};

/** 按节点补齐目标时段素材；回填前再次确认人工内容和业务上下文没有变化。 */
export async function prepareSummaryDescriptions(options: SummaryCompositionOptions): Promise<boolean> {
  const { dayId, slot, getEditor, onChange, completePointDescriptions, signal } = options;
  if (slot === "daytime") return true;
  const initial = getEditor();
  const initialDay = initial.days.find((day) => day.id === dayId);
  if (!initialDay) return false;
  const isCurrent = () => !signal?.aborted && (options.isCurrent?.() ?? true)
    && getEditor().id === initial.id;
  const missing = initialDay.nodes.filter((node) => (slot === "full_day" || node.timeSlot === slot) && node.nodeType === "point" && !hasTextContent(node.description));
  const outcomes = await Promise.allSettled(missing.map(async (node) => {
    if (!isCurrent()) return false;
    const before = getEditor();
    const currentNode = before.days.find((day) => day.id === dayId)?.nodes.find((item) => item.id === node.id);
    if (hasTextContent(currentNode?.description)) return true;
    if (!completePointDescriptions || !currentNode?.pointSpuId?.trim()) {
      throw new Error("点位介绍尚未补齐，原正文已保留。请先补充点位介绍后重试。");
    }
    const baseline = pointDescriptionFingerprint(before, dayId, node.id);
    const result = await completePointDescriptions({ dayId, nodeIds: [node.id], overwrite: true, editor: before }, signal);
    if (!isCurrent() || pointDescriptionFingerprint(getEditor(), dayId, node.id) !== baseline) return false;
    const description = result.items.find((item) => item.nodeId === node.id && item.status === "generated")?.description;
    if (!hasTextContent(description)) throw new Error("点位介绍生成失败，原正文已保留。请先补齐介绍后重试。");
    const latest = getEditor();
    onChange({ ...latest, days: latest.days.map((day) => day.id === dayId ? { ...day,
      nodes: day.nodes.map((item) => item.id === node.id ? { ...item, description } : item),
    } : day) });
    return true;
  }));
  const failure = outcomes.find((outcome) => outcome.status === "rejected");
  if (failure?.status === "rejected") throw failure.reason;
  return outcomes.every((outcome) => outcome.status === "fulfilled" && outcome.value);
}

/** 先补学习材料再融合正文，任何请求期间的人工改动都使旧响应失效。 */
export async function composeDaySummary(options: SummaryCompositionOptions): Promise<"updated" | "empty" | "stale" | "manual"> {
  const { dayId, slot, getEditor, onChange, enrich, signal, automatic } = options;
  const initial = getEditor();
  const field = summaryFieldBySlot[slot];
  const sourceField = summarySourceFieldBySlot[slot];
  const initialDay = initial.days.find((day) => day.id === dayId);
  if (!initialDay) return "stale";
  if (automatic && !options.forceManualRewrite && isManualSummarySource(effectiveSummarySource(initialDay, slot))) return "manual";
  const expectedValue = effectiveSummaryText(initialDay, slot) ?? null;
  const expectedSource = effectiveSummarySource(initialDay, slot);
  const isCurrent = () => !signal?.aborted && (options.isCurrent?.() ?? true)
    && getEditor().id === initial.id
    && getEditor().days.some((day) => day.id === dayId && day.isFullDay === initialDay.isFullDay
      && (effectiveSummaryText(day, slot) ?? null) === expectedValue
      && effectiveSummarySource(day, slot) === expectedSource);
  if (slot !== "daytime" && !hasSummaryArrangement(initial, dayId, slot)) {
    if (!initialDay.legacyCompatibility && hasTextContent(effectiveSummaryText(initialDay, slot))) {
      onChange({ ...initial, days: initial.days.map((day) => day.id === dayId ? { ...day, legacyCompatibility: false, [field]: slot === "full_day" ? "" : null, [sourceField]: "ai_composed" } : day) });
    }
    return "empty";
  }
  const hasMissingDescriptions = slot !== "daytime" && initialDay.nodes.some((node) => (slot === "full_day" || node.timeSlot === slot)
    && node.nodeType === "point" && !hasTextContent(node.description));
  if (hasMissingDescriptions && !options.descriptionsPrepared && !await prepareSummaryDescriptions({ ...options, isCurrent })) return "stale";
  if (slot !== "daytime" && getEditor().days.find((day) => day.id === dayId)?.nodes
    .some((node) => (slot === "full_day" || node.timeSlot === slot) && node.nodeType === "point" && !hasTextContent(node.description))) {
    throw new Error("点位介绍尚未补齐，原正文已保留。请先补充点位介绍后重试。");
  }
  if (!isCurrent()) return "stale";
  const prepared = getEditor();
  const baseline = summaryMaterialFingerprint(prepared, dayId, slot);
  const result = await requestDaySummaryRewrite(prepared, dayId, slot, enrich, signal);
  if (!isCurrent() || summaryMaterialFingerprint(getEditor(), dayId, slot) !== baseline) return "stale";
  const suggestion = result.days.find((day) => day.dayId === dayId);
  if (slot !== "daytime" && suggestion?.composedSlots?.includes(slot)
    && suggestion.emptySlots?.includes(slot) && !hasTextContent(suggestion[field])) {
    // 只有服务端事实投影确认的空结果可以清理旧稿，模型漏字或空响应仍按失败处理。
    const current = getEditor();
    onChange({ ...current, days: current.days.map((day) => day.id === dayId
      ? { ...day, legacyCompatibility: false, [field]: slot === "full_day" ? "" : null, [sourceField]: "ai_composed" } : day) });
    return "empty";
  }
  const next = applyGeneratedSummary(getEditor(), dayId, slot, result, expectedValue);
  if (!next) throw new Error(slot === "daytime"
    ? "未能生成本日摘要：没有可用的上午或下午安排。请先补充白天行程后重试，原摘要已保留。"
    : "AI 未返回有效正文，原内容已保留。请核对该时段的点位与交通后重试。");
  onChange(next);
  return "updated";
}
